import { preloadComponents, criticalComponents } from '@/components/lazy';

// Route components for preloading
const routeImports: Record<string, () => Promise<any>> = {
  '/': () => import('@/pages/HomePage'),
  '/login': () => import('@/pages/LoginPage'),
  '/sftp-sync': () => import('@/pages/sftp-sync'),
  '/on-hand-process': () => import('@/pages/on-hand-process'),
  '/csv-import': () => import('@/features/csv-import'),
};

// Preload route on hover
export function preloadRoute(path: string): void {
  const importFn = routeImports[path];
  if (importFn && typeof window !== 'undefined') {
    requestIdleCallback(() => {
      importFn().catch((err: any) => {
        console.warn(`Failed to preload route ${path}:`, err);
      });
    }, { timeout: 1000 });
  }
}

// Preload critical routes after initial load
export function preloadCriticalRoutes(): void {
  if (typeof window !== 'undefined') {
    // Wait for initial render to complete
    requestIdleCallback(() => {
      // Preload commonly used routes
      const criticalRoutes = ['/sftp-sync', '/on-hand-process'];
      criticalRoutes.forEach(route => {
        const importFn = routeImports[route];
        if (importFn) {
          importFn().catch((err: any) => {
            console.warn(`Failed to preload critical route ${route}:`, err);
          });
        }
      });

      // Preload critical grid components
      preloadComponents(criticalComponents);
    }, { timeout: 3000 });
  }
}

// Add route preloading to navigation links
export function addRoutePreloading(): void {
  if (typeof window !== 'undefined') {
    document.addEventListener('mouseover', (e) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement;
      
      if (link && link.href.startsWith(window.location.origin)) {
        const path = new URL(link.href).pathname;
        const basePath = '/' + path.split('/')[1];
        
        if (routeImports[basePath]) {
          preloadRoute(basePath);
        }
      }
    }, { passive: true });
  }
}

// Intersection Observer for lazy loading components
export function setupIntersectionObserver(): void {
  if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const element = entry.target as HTMLElement;
            const lazyComponent = element.getAttribute('data-lazy-component');
            
            if (lazyComponent) {
              // Trigger component preload
              element.dispatchEvent(new CustomEvent('lazy-load', { detail: lazyComponent }));
              observer.unobserve(element);
            }
          }
        });
      },
      { rootMargin: '50px' }
    );

    // Observe elements with lazy loading attribute
    document.querySelectorAll('[data-lazy-component]').forEach(el => {
      observer.observe(el);
    });
  }
}

// Request idle callback polyfill
if (typeof window !== 'undefined' && !('requestIdleCallback' in window)) {
  (window as any).requestIdleCallback = (callback: IdleRequestCallback, options?: IdleRequestOptions) => {
    const start = Date.now();
    return setTimeout(() => {
      callback({
        didTimeout: false,
        timeRemaining: () => Math.max(0, 50 - (Date.now() - start)),
      });
    }, options?.timeout || 1);
  };

  (window as any).cancelIdleCallback = (id: number) => {
    clearTimeout(id);
  };
}